import React from 'react';
import { Controller, Path, PathValue } from 'react-hook-form';
import { observer } from 'mobx-react';
import { Checkbox } from '@alfa-retail-react/components';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { ValidationRules } from 'app/form/ValidationRules';

import './ConditionAgreementFormItem.less';

export interface ConditionAgreementFormItemProps<T> {
    /**
     * Лэйбл чекбокса.
     */
    label: React.ReactNode;

    /**
     * Значение по умолчанию.
     */
    defaultValue?: PathValue<T, Path<T>>;

    /**
     * Выбранное значение.
     */
    checked: boolean;

    /**
     * Изменение значения чекбокса.
     */
    onChange: (checked: boolean) => void;

    /**
     * CSS класс.
     */
    className?: string;

    /**
     * Имя поля формы.
     * Является ключом одного из атрибутов объекта типа T
     */
    name: Path<T>;

    /**
     * Пользовательское сообщение валидации
     */
    validationErrorMessage?: string;

    /**
     * Обязательность поля
     */
    isRequired?: boolean;
}

export const CONDITION_AGREEMENT_FIELD_NAME = 'conditionAgreement';
export const MARKETING_COMMUNICATION_FIELD_NAME = 'marketingCommunication';
const DEFAULT_VALIDATION_ERROR_MESSAGE = 'Ваше согласие обязательно';

/**
 * Компонент для отображения элемента формы по условию соглашения.
 */
export const ConditionAgreementFormItem = observer(
    <T, TT>({
        label,
        defaultValue,
        checked,
        onChange,
        className,
        name,
        validationErrorMessage,
        isRequired = true,
    }: ConditionAgreementFormItemProps<T>) => {
        const labelElement = (
            <span className={`${CONDITION_AGREEMENT_FIELD_NAME}Content`}>
                {label}
            </span>
        );

        const { control, handleMultipleErrors, setFieldElement } =
            useFormContext<T>();

        const rules = isRequired
            ? {
                  required: ValidationRules.required(
                      validationErrorMessage ||
                          DEFAULT_VALIDATION_ERROR_MESSAGE,
                  ),
              }
            : null;

        return (
            <Controller<T>
                name={name}
                control={control}
                defaultValue={defaultValue}
                rules={rules}
                render={({ field, fieldState }) => (
                    <Checkbox
                        ref={(element) => {
                            field.ref(element);
                            setFieldElement(name, element);
                        }}
                        className={classNames(
                            CONDITION_AGREEMENT_FIELD_NAME,
                            className,
                        )}
                        size="m"
                        error={handleMultipleErrors(fieldState.error)}
                        checked={checked}
                        label={labelElement}
                        onChange={(event, payload) => {
                            field.onChange(event);
                            runInAction(() => {
                                onChange(payload.checked);
                            });
                        }}
                    />
                )}
            />
        );
    },
);
